import { Lightning } from '@lightningjs/sdk';
import _merge from 'lodash/merge';
import _get from 'lodash/get';
import { t } from '../../../../i18n';
import HeroOverlay from '../../../hero/heroOverlay/heroOverlay.component';
import Progress from '../../../progress/progress.component';
import Icon from '../../../icon/icon.component';
import { CallToAction } from '../../../callToAction/primary/callToAction.component';
import { identity } from '../../../utils/sizes';
import { cover, contain } from '../../../utils/textures';
import {
  templates,
  sizes,
  logoOptions,
  imageOptions,
  Z_INDEX_NORMAL,
  Z_INDEX_WITH_PROGRAM_INFO,
} from '../../solo.constants';
import { makeTextPatch } from '../../../utils/templates';
import { isVisibleDetails, isVisibleIcon, isVisibleCTA } from '../../solo.utils';
import { makeProgressBarProperties } from '../../../progress/progress.utils';
import { ProgramInformation } from '../../../programInformation/programInformation.component';
import { PROGRAM_INFORMATION_CLOSED_SIGNAL } from '../../../programInformation/programInformation.constants';
import { SELECTED_SIGNAL } from '../../../signals';
import { MyListButtonPrimary } from '../../../myListCallToAction/myListCallToAction.component';
import { sendEvent } from '../../../../../logic/analytics/analytics.utils';
import {
  getProgramInformationFocusedStyle,
  getProgramInformationUnFocusedStyle,
} from '../../../programInformation/programInformation.style';
import {
  colors,
  defaultContext,
  getServiceIconViewBox,
  getBasicIconViewBox,
  getInitialStyle,
  getTitleWordWrapWidth,
  SERVICE_ICON_MARGIN_RIGHT,
} from './solo.style';

/**
 * Solo
 *
 * Large-size rectangular component representing a single content entry
 * @see https://app.abstract.com/projects/162522cb-a770-4688-9ead-06e1fd627b20/branches/master/collections/db8488b1-1765-4609-aad7-8b89d15a0903
 */
export default class Solo extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Gradient: {
          type: HeroOverlay,
          w: identity,
          h: identity,
        },
        FlexContainer: {
          Logo: {},
          DetailsFlexContainer: {
            ServiceIcon: {
              type: Icon,
            },
            Description: {},
            BasicIcon: {
              type: Icon,
            },
          },
          Title: {},
          ProgressBar: {
            type: Progress,
            visible: false,
          },
          CTAFlexContainer: {
            PlayButton: {
              type: CallToAction,
            },
            MyListButton: {
              type: MyListButtonPrimary,
              passSignals: {
                [SELECTED_SIGNAL]: true,
              },
            },
            InfoButton: {
              type: CallToAction,
            },
          },
        },
        ProgramInformation: {
          type: ProgramInformation,
          signals: {
            [PROGRAM_INFORMATION_CLOSED_SIGNAL]: '_handleProgramInformationClose',
          },
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._buttons = [];
    this._index = 0;
    this._style = defaultContext;
  }

  _init() {
    // NOTE: needs to use txLoaded event to add margin
    const serviceIcon = this.tag('ServiceIcon');
    serviceIcon.on('txLoaded', () => {
      serviceIcon.patch({
        flexItem: {
          marginRight: SERVICE_ICON_MARGIN_RIGHT[this._style.screenSize],
        },
      });
    });
  }

  _detach() {
    this.tag('ServiceIcon').removeAllListeners('txLoaded');
  }

  get item() {
    return this._item;
  }

  set item({ content, blockIndex, itemIndex, config, colorScheme = defaultContext.colorMode }) {
    this._item = content;
    const { image, title, logo, progress, action, csaPicto, servicePicto, extraDetails, bookmark, description } =
      this._item;
    const textColor = colors.text[colorScheme];
    const { screenSize } = this._style;
    const logoSrc = (logo && logo.src) || '';
    const titleWidth = getTitleWordWrapWidth(screenSize);

    this.patch({
      texture: cover(image.src, config.w, config.h),
      FlexContainer: {
        Logo: {
          visible: Boolean(logoSrc),
          texture: {
            ...contain(logoSrc, logoOptions[screenSize].width, logoOptions[screenSize].height),
            hasAlpha: true,
          },
        },
        DetailsFlexContainer: {
          visible: isVisibleDetails(servicePicto, csaPicto, extraDetails),
          ServiceIcon: {
            visible: isVisibleIcon(servicePicto),
            type: Icon,
            iconData: {
              picto: servicePicto,
              viewBox: getServiceIconViewBox(screenSize),
            },
          },
          Description: {
            visible: Boolean(extraDetails),
            text: {
              text: extraDetails,
            },
          },
          BasicIcon: {
            visible: isVisibleIcon(csaPicto),
            type: Icon,
            iconData: {
              picto: csaPicto,
              viewBox: getBasicIconViewBox(screenSize),
            },
          },
        },
        Title: {
          ...makeTextPatch(title, titleWidth, textColor),
        },
        ProgressBar: {
          ...makeProgressBarProperties(progress),
        },
        CTAFlexContainer: {
          PlayButton: {
            type: CallToAction,
            visible: isVisibleCTA(action),
            label: _get(action, 'label', ''),
            picto: _get(action, 'picto', ''),
          },
          MyListButton: {
            type: CallToAction,
            visible: Boolean(bookmark),
            label: t('bookmarks.actionLabel'),
            item: bookmark,
            blockIndex,
            itemIndex,
          },
          InfoButton: {
            visible: Boolean(title) || Boolean(description),
            type: CallToAction,
          },
        },
      },
      ProgramInformation: {
        data: this._item,
      },
    });
    const items = this.tag('CTAFlexContainer').children;
    this._buttons = items.filter(item => item.visible);
  }

  set contentPadding(value) {
    this.tag('FlexContainer').x = Number.isFinite(value) ? value : 0;
  }

  _focus() {
    this._setIndex();
  }

  _getFocused() {
    return this._focused;
  }

  _handleRight() {
    if (this._index < this._buttons.length - 1) {
      this._setIndex(this._index + 1);

      return true;
    }

    return false;
  }

  _handleLeft() {
    if (this._index > 0) {
      this._setIndex(this._index - 1);

      return true;
    }

    return false;
  }

  _setIndex(index = this._index) {
    this._index = index;
    this._focused = this._buttons[this._index];
    this._refocus();
  }

  _handleEnter() {
    if (this._focused === this.tag('InfoButton')) {
      this._showProgramInformation();
      sendEvent({ key: 'pressMoreInfo' });
    } else {
      this.signal(SELECTED_SIGNAL, this._item);
    }
  }

  _showProgramInformation() {
    this.zIndex = Z_INDEX_WITH_PROGRAM_INFO;
    this.tag('ProgramInformation').patch(getProgramInformationFocusedStyle());
    this._focused = this.tag('ProgramInformation');
  }

  _handleProgramInformationClose() {
    this.zIndex = Z_INDEX_NORMAL;
    this.tag('ProgramInformation').patch(getProgramInformationUnFocusedStyle());
    this._setIndex(this._buttons.indexOf(this.tag('InfoButton')));
    sendEvent({ key: 'closeMoreInfo' });
  }
}

Solo.Templates = templates;
Solo.Sizes = sizes;
Solo.ImageOptions = imageOptions;
